import React from 'react'
import { Link } from 'react-router-dom'
import Divider from 'src/components/ui/Divider'
import GoinFlow from 'src/components/icons/ui/GoinFlow'
import Facebook from 'src/components/icons/ui/Facebook'
import Instagram from 'src/components/icons/ui/Instagram'
import Twitter from 'src/components/icons/ui/Twitter'
import Phone from 'src/components/icons/ui/Phone'
import Envelope from 'src/components/icons/ui/Envelope'
import { useBreakpoint } from '../hooks'
import { RIDER_PORTAL_MAIN_PATH, RIDER_PORTAL_UI_MESSAGES } from 'src/constants/rider-portal'

const Footer = () => {
  const {
    aboutUs,
    accessibility,
    allRightsReserved,
    contactInfo,
    contactUs,
    flow,
    goin,
    goinMyWay,
    goinTransitFlowPro,
    faq,
    home,
    quickLinks,
    ourProducts,
    privacy,
    privacyPolicy,
    products,
    tagLine,
    terms,
    termsOfService
  } = RIDER_PORTAL_UI_MESSAGES.footer

  const device = useBreakpoint()
  const isMobile = device === 'sm' || device === 'md'

  const socialMedia = [
    {
      name: 'Facebook',
      icon: <Facebook className='cursor--pointer icon-facebook svg-color-white' />,
      path: '#'
    },
    {
      name: 'Twitter',
      icon: <Twitter className='cursor--pointer icon-twitter svg-color-white' />,
      path: '#'
    },
    {
      name: 'Instagram',
      icon: <Instagram className='cursor--pointer icon-instagram svg-color-white' />,
      path: '#'
    }
  ]

  const quickLinksAndProducts = [
    {
      title: quickLinks,
      links: [
        { label: home, path: RIDER_PORTAL_MAIN_PATH },
        { label: goinMyWay, path: `${RIDER_PORTAL_MAIN_PATH}/booking` },
        { label: ourProducts, path: `${RIDER_PORTAL_MAIN_PATH}/products` },
        { label: aboutUs, path: `${RIDER_PORTAL_MAIN_PATH}/about` },
        { label: privacyPolicy, path: `${RIDER_PORTAL_MAIN_PATH}/privacy` }
      ]
    },
    {
      title: products,
      links: [
        { label: goinTransitFlowPro, path: `${RIDER_PORTAL_MAIN_PATH}/transit-flow-pro` },
        { label: goinMyWay, path: `${RIDER_PORTAL_MAIN_PATH}/booking` },
        { label: faq, path: `${RIDER_PORTAL_MAIN_PATH}/faq` },
        { label: termsOfService, path: `${RIDER_PORTAL_MAIN_PATH}/terms` },
        { label: contactUs, path: `${RIDER_PORTAL_MAIN_PATH}/contact` }
      ]
    }
  ]

  const contactInfoCol = [
    {
      title: contactInfo,
      links: [
        {
          icon: <Phone className='svg-color-green1' />,
          label: '(555) 123-4567',
          path: '#',
          type: 'phone'
        },
        {
          icon: <Envelope className='svg-color-green1' />,
          label: 'support@goinflow.com',
          path: '#',
          type: 'email'
        }
      ]
    }
  ]

  const ctaLinks = [
    { label: accessibility, path: `${RIDER_PORTAL_MAIN_PATH}/accessibility` },
    { label: privacy, path: `${RIDER_PORTAL_MAIN_PATH}/privacy` },
    { label: terms, path: `${RIDER_PORTAL_MAIN_PATH}/terms` }
  ]

  return (
    <footer className='bg-black3 mbsc-grid no-padding rider-portal-footer-container'>
      <div className='rider-portal-footer-row'>
        <div className={`mbsc-row ${isMobile ? 'row-gap-40' : ''}`}>
          <div className='mbsc-col-12 mbsc-col-md-6 mbsc-col-lg-3'>
            <div className='flex column-gap-8 items-center'>
              <GoinFlow className='svg-color-white' />
              <span>
                <span className='goin font-white text-2xl'>{goin}</span>
                <span className='font-white text-2xl weight-300'>{` ${flow}`}</span>
              </span>
            </div>
            <p className={`mt1 pt2 text-white ${isMobile ? 'text-center' : ''}`}>{tagLine}</p>
            <div className={`flex column-gap-16 pt1 ${isMobile ? 'justify-center' : ''}`}>
              {socialMedia.map(({ icon, name, path }) => (
                <Link aria-label={`Visit our ${name} page`} className='social-media-link' key={name} to={path}>
                  {icon}
                </Link>
              ))}
            </div>
          </div>
          {quickLinksAndProducts.map(({ title, links }) => (
            <div className='mbsc-col-12 mbsc-col-sm-6 mbsc-col-lg-3' key={title}>
              <h3 className='font-bold font-white mb1 mt0 text-center text-lg'>{title}</h3>
              {links.map(({ label, path }) => (
                <div className='pt2 text-center' key={`${label}-${title}`}>
                  <Link className='cursor--pointer footer-link text-center text-white' to={path}>
                    {label}
                  </Link>
                </div>
              ))}
            </div>
          ))}
          {contactInfoCol.map(({ title, links }) => (
            <div className='mbsc-col-12 mbsc-col-sm-6 mbsc-col-lg-3' key={title}>
              <h3 className='font-bold font-white mb1 mt0 text-center text-lg'>{title}</h3>
              {links.map(({ label, icon, path, type }) => (
                <div
                  className={`flex column-gap-16 pt2 items-center ${isMobile ? 'justify-center' : ''}`}
                  key={`${label}-${title}`}
                >
                  {icon}
                  <Link
                    aria-label={type === 'phone' ? `Call us at ${label}` : `Email us at ${label}`}
                    className={`text-white ${type === 'email' ? 'cursor--pointer footer-link' : ''}`}
                    to={path}
                  >
                    {label}
                  </Link>
                </div>
              ))}
            </div>
          ))}
        </div>
        <div className='mbsc-row'>
          <div className='mbsc-col-12'>
            <div className='mb3 mt3 pt2'>
              <Divider />
            </div>
          </div>
          <div className='mbsc-col-12 mbsc-col-md-6'>
            <div className={`text-sm text-white ${isMobile ? 'text-center' : ''}`}>
              &copy;{` ${new Date().getFullYear()} ${goin}. ${allRightsReserved}`}
            </div>
          </div>
          <div className='mbsc-col-12 mbsc-col-md-6'>
            <div className={`column-gap-24 flex ${isMobile ? 'justify-center pt2' : 'justify-end'}`}>
              {ctaLinks.map(({ label, path }) => (
                <Link
                  aria-label={`View ${label.toLowerCase()} information`}
                  className='cursor--pointer footer-link text-sm text-white'
                  key={label}
                  to={path}
                >
                  {label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer



// WEBPACK FOOTER //
// ./src/components/riders/rider-portal/common/Footer.tsx